import { Injectable } from '@angular/core';
import { Config } from '../universal-config';
import { Store, select } from '@ngrx/store';
import { IAppState } from '../app.state';
import { openDialog, closeDialog } from '../common/dialogs/dialogs.actions';
import { selectAllAvailableWorkspaces } from '../workspaces/workspaces.selector';
import { map } from 'rxjs/operators';
import { selectCurrentNote } from './models';

enum WS_TYPE {
  BUSSINESS,
  PERSONAL,
  SHARED,
}

enum SUBSCRIPTION_TYPE {
  PRO,
  FREE,
}

enum SHARE_TYPE {
  NOTE,
  FOLDER,
}

enum LIMIT_ERROR_TYPE {
  USAGE = 'Usage',
  NOTES_NUMBER = 'NotesNumber',
  ATTACHMENT = 'Attachment',
  DISABLED = 'Disabled',
}

@Injectable()
export class ShareErrorsService {

  limitErrors;
  workspaces;
  organizations;
  currentNote;

  constructor(
    public _store: Store<IAppState>,
  ) {

    this.limitErrors = {
      bussinesWs: {
        note: {
          usage: `The page can't be saved because the organization owner has reached the traffic limit. Contact support service team@nimbusweb.co for more details.`,
          attachment: `You cannot save the page because there are files larger than 1GB. Contact support service team@nimbusweb.co for more details.`,
          disabled: `You can't save page because the organization is not active.`,
        },
        folder: {
          usage: `The folder can't be saved because the organization owner has reached the traffic limit. Contact support service team@nimbusweb.co for more details.`,
          attachment: `You cannot save folder because there are pages with files larger than 1GB. Contact support service team@nimbusweb.co for more details.`,
          disabled: `You can't save page because the organization is not active.`,
        }
      },
      personalWs: {
        free: {
          note: {
            usage: `You have reached your monthly traffic limit. To continue, you have to purchase FuseBase Standard.`,
            notesNumber: `You've reached 30 pages limit per workspace for Free accounts. Upgrade to Pro account to have unlimited pages and unlock all Premium benefits!`,
            attachment: `You cannot save the page because there are files larger than 10MB. Upgrade to FuseBase Standard to save this page.`,
          },
          folder: {
            usage: `You have reached your monthly traffic limit. To continue, you have to purchase FuseBase Standard.`,
            notesNumber: `You've reached 30 pages limit per workspace for Free accounts. Upgrade to Pro account to have unlimited pages and unlock all Premium benefits!`,
            attachment: `You cannot save the folder because there are pages with files larger than 10MB. Upgrade to FuseBase Standard to save this page.`,
          }
        },
        pro: {
          note: {
            usage: `You cannot save the page because you have almost reached the traffic limit.`,
            attachment: `You cannot save pages because there are files larger than 1GB.`,
          },
          folder: {
            usage: `You cannot save the folder because you have almost reached the traffic limit.`,
            attachment: `You cannot save pages because there are pages with files larger than 1GB.`,
          }
        },
      },
      sharedWs: {
        free: {
          note: {
            usage: `The page can't be saved because the workspace owner has reached the traffic limit. Contact workspace owner for more details.`,
            notesNumber: `Sorry, you can't create additional pages because this workspace has reached the maximum page limit. Contact the workspace owner for more details`,
            attachment: `You cannot save the page because there are files larger than 10MB. Contact workspace owner for more details.`,
          },
          folder: {
            usage: `The folder can't be saved because the workspace owner has reached the traffic limit. Contact workspace owner for more details.`,
            notesNumber: `Sorry, you can't create additional pages because this workspace has reached the maximum page limit. Contact the workspace owner for more details.`,
            attachment: `You cannot save the folder because there are pages with files larger than 10MB. Contact workspace owner for more details.`,
          }
        },
        pro: {
          note: {
            usage: `The page can't be saved because the workspace owner has reached the traffic limit. Contact workspace owner for more details.`,
            attachment: `You cannot save the page because there are files larger than 1GB. Contact workspace owner for more details.`,
          },
          folder: {
            usage: `The folder can't be saved because the workspace owner has reached the traffic limit. Contact workspace owner for more details.`,
            attachment: `You cannot save pages because there are pages with files larger than 1GB. Contact workspace owner for more details.`,
          }
        },
      },
    }
    const workspaces$ = this._store.pipe(select(selectAllAvailableWorkspaces));
    const organizations$ = this._store.pipe(select(selectAllAvailableWorkspaces));
    const currentNote$ = this._store.pipe(select(selectCurrentNote));
    workspaces$.subscribe((workspaces) => {
      this.workspaces = workspaces;
    });
    organizations$.subscribe((organizations) => {
      this.organizations = organizations;
    })
    currentNote$.subscribe((currentNote) => {
      this.currentNote = currentNote;
    })
  }

  showError(error) {
    const errorMessage = error.message;
    if(errorMessage.name == 'LimitError') {
      this.showLimitError(error);
    } else if(errorMessage.name == 'MultiError') {
      this.showError({message: error.message.errors[0], workspaceId: error.workspaceId, userId: error.userId});
    } else {
      this.showDefaultError();
    }
  }

  showAttachmentsCountLimitError(uploadCount: number, maxCount: number) {
    const dialogOptions = {
      text: `You are trying to upload ${uploadCount} files when maximum allowed number is ${maxCount}.<br/>Please, try to upload again.`,
      okButtonText: 'Ok',
      hideCancelButton: true,
      onSuccess: () => {
        this._store.dispatch(closeDialog());
      }
    }
    this._store.dispatch(openDialog(dialogOptions));
  }

  showDefaultError() {
    const dialogOptions = {
      text: 'An error occurred while saving the public page. Try saving again. We are sorry for the inconvenience.',
      okButtonText: 'Ok',
      hideCancelButton: true,
      onSuccess: () => {
        this._store.dispatch(closeDialog());
      }
    }
    this._store.dispatch(openDialog(dialogOptions));
  }

  showLimitError(error) {
    const limitErrorTitle = this.getLimitErrorTitle(error.message.objectType);
    const limitErrorText = this.getLimitErrorText(error.message, error.userId, error.workspaceId);
    const isFreeAccount = this.getSubscriptionType(error.workspaceId) == SUBSCRIPTION_TYPE.FREE;
    const workspace = this.getWorkspaceById(error.workspaceId);
    const isOwner = error.userId == workspace.userId;
    const showPricing = isOwner && isFreeAccount;

    const dialogOptions = {
      title: limitErrorTitle,
      text: limitErrorText,
      okButtonText: showPricing ? 'Go to FuseBase Standard' : 'Ok',
      onSuccess: () => {
        if(showPricing) {
          window.open('https://nimbusweb.me/pricing.php');
          this._store.dispatch(closeDialog());
        } else {
          this._store.dispatch(closeDialog());
        }
      }
    }
    this._store.dispatch(openDialog(dialogOptions));
  }

  getLimitErrorTitle(errorType){
    switch (errorType) {
      case LIMIT_ERROR_TYPE.USAGE: return 'Limit traffic error';
      case LIMIT_ERROR_TYPE.NOTES_NUMBER: return 'Pages limit reached';
      case LIMIT_ERROR_TYPE.ATTACHMENT: return 'Attachment size too big';
      case LIMIT_ERROR_TYPE.DISABLED: return 'Organization disabled';
    }
  }

  getLimitErrorText(error, userId, workspaceId) {
    const errorsByWsType = this.getLimitErrorsByWsType(workspaceId, userId, this.limitErrors);
    const errorsBySubscriptionType = this.getLimitErrorsBySubscriptionType(errorsByWsType, workspaceId);
    const errorsByShareType = this.getLimitErrorsByShareType(errorsBySubscriptionType);
    const errorText = this.getLimitErrorTextByErrorType(error.objectType, errorsByShareType);
    return errorText;
  }

  getLimitErrorsByWsType(workspaceId, userId, errors) {
    const wsType = this.getWsType(workspaceId, userId);
    switch (wsType) {
      case WS_TYPE.BUSSINESS: return errors.bussinesWs;
      case WS_TYPE.PERSONAL: return errors.personalWs;
      case WS_TYPE.SHARED: return errors.sharedWs;
    }
  }

  getLimitErrorsBySubscriptionType(errors, workspaceId) {
    const subscriptionType = this.getSubscriptionType(workspaceId);
    switch (subscriptionType) {
      case SUBSCRIPTION_TYPE.FREE: return errors.free;
      case SUBSCRIPTION_TYPE.PRO: return errors.pro;
    }
  }

  getLimitErrorsByShareType(errors) {
    const shareType = this.getShareType();
    switch (shareType) {
      case SHARE_TYPE.NOTE: return errors.note;
      case SHARE_TYPE.FOLDER: return errors.folder;
    }
  }

  getLimitErrorTextByErrorType(errorType, errors) {
    switch (errorType) {
      case LIMIT_ERROR_TYPE.USAGE: return errors.usage;
      case LIMIT_ERROR_TYPE.NOTES_NUMBER: return errors.notesNumber;
      case LIMIT_ERROR_TYPE.ATTACHMENT: return errors.attachment;
    }
  }

  getWsType(workspaceId, userId) {
    const workspace = this.getWorkspaceById(workspaceId);
    if (this.organizations && this.organizations.length) {
      const organization = this.organizations.find((organization) => organization.globalId == workspace.orgId);
      if(organization) {
        return WS_TYPE.BUSSINESS;
      } else {
        if(workspace.userId == userId) {
          return WS_TYPE.PERSONAL;
        } else {
          return WS_TYPE.SHARED;
        }
      }
    }
  }

  getSubscriptionType(workspaceId) {
    const workspace = this.getWorkspaceById(workspaceId);
    return workspace.hasActivePremium ? SUBSCRIPTION_TYPE.PRO : SUBSCRIPTION_TYPE.FREE;
  }

  getShareType() {
    const type = this.currentNote.type;
    return type == 'note' ? SHARE_TYPE.NOTE : SHARE_TYPE.FOLDER;
  }

  getWorkspaceById(workspaceId){
    return this.workspaces.find((workspace) => workspace.globalId == workspaceId);
  }
}
